import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import VariantInventoryModal from "../../components/CustomComponents/Inventory/VariantInventoryModal";
import AdjustStockModal from "../../components/CustomComponents/modal/AdjustStockModal";
import { useAssignedBy } from "../../hooks/useStaffData";
import DateRangePicker from "../../components/CustomComponents/DateRangePicker/DateRangePicker";
import { Panel, PanelHeader, PanelBody } from "../../components/panel/panel";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import moment from "moment";
import {
  formatCompanyDateTimeForApi,
  formatLedgerRowDateTime,
  getRowDateTimestamp,
} from "../../utils/companyDateTime";

const toInt = (v) => {
  if (v === null || v === undefined) return 0;
  const n = typeof v === "number" ? v : parseInt(v, 10);
  return Number.isFinite(n) ? n : 0;
};

const parseNullableFloat = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = typeof v === "number" ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

const getPublishMode = (branchPublish) => {
  if (branchPublish === null || branchPublish === undefined) return "inherit";
  return branchPublish ? "publish" : "unpublish";
};

const buildInitialOverrideForm = (item) => {
  const branchPublish = item?.branchPublish ?? item?.BranchPublish;
  return {
    branchPublishMode: getPublishMode(branchPublish),
    branchOrderby: item?.branchOrderby ?? item?.BranchOrderby ?? "",
    isPriceOverrideActive: item?.branchIsPriceOverrideActive ?? item?.BranchIsPriceOverrideActive ?? false,
    branchPriceOverride: item?.branchPriceOverride ?? item?.BranchPriceOverride ?? "",
    branchDeliveryPriceOverride: item?.branchDeliveryPriceOverride ?? item?.BranchDeliveryPriceOverride ?? "",
    branchTakeAwayPriceOverride: item?.branchTakeAwayPriceOverride ?? item?.BranchTakeAwayPriceOverride ?? "",
  };
};

const BranchEditItem = () => {
  const { itemId } = useParams();
  const navigate = useNavigate();

  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const assignedBy = useAssignedBy();
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();

  const [loading, setLoading] = useState(true);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [parentItem, setParentItem] = useState(null);
  const [variantItems, setVariantItems] = useState([]);
  const [variantInventoryModalOpen, setVariantInventoryModalOpen] = useState(false);
  const [selectedVariantForInventory, setSelectedVariantForInventory] = useState(null);

  const [activeTab, setActiveTab] = useState("overrides");
  const [comboProducts, setComboProducts] = useState([]);
  const [ledgerData, setLedgerData] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerDateRange, setLedgerDateRange] = useState(() => [
    moment().startOf("month").startOf("day").toDate(),
    moment().endOf("month").endOf("day").toDate(),
  ]);
  const [selectedLedgerVariant, setSelectedLedgerVariant] = useState({ value: "all", label: "All" });
  const [showAdjustModal, setShowAdjustModal] = useState(false);

  const { getCompCurrency, getCompTimeZone, getCompUTCTimeZone } = useProfileAndSettings();
  const compCurrency = getCompCurrency();
  const compTimeZone = getCompTimeZone();
  const compUTCTimeZone = getCompUTCTimeZone();
  const ledgerTzOptions = useMemo(
    () => ({
      compTimeZone:
        companyProfile?.compTimeZone ?? companyProfile?.CompTimeZone ?? compTimeZone,
      compUTCTimeZone:
        companyProfile?.compUTCTimeZone ?? companyProfile?.CompUTCTimeZone ?? compUTCTimeZone,
    }),
    [companyProfile, compTimeZone, compUTCTimeZone]
  );
  const currencySymbols = { GBP: "£", USD: "$", EUR: "€" };
  const getCurrencySymbol = (code) => currencySymbols[code] || code;
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  const openVariantInventoryModal = (variant) => {
    setSelectedVariantForInventory(variant || null);
    setVariantInventoryModalOpen(true);
  };

  const closeVariantInventoryModal = () => {
    setVariantInventoryModalOpen(false);
    setSelectedVariantForInventory(null);
  };

  // form state
  const [parentForm, setParentForm] = useState(buildInitialOverrideForm(null));
  const [variantForms, setVariantForms] = useState({}); // { [menuItemId]: form }

  const parentCompId = useMemo(() => toInt(companyProfile?.parentComID ?? companyProfile?.ParentComID), [companyProfile]);
  const isBranchMode = parentCompId > 0;
  const hasVariants = useMemo(
    () => !!(parentItem?.menuItem_HasVariant ?? parentItem?.MenuItem_HasVariant),
    [parentItem]
  );
  const isComboItem = useMemo(
    () => (parentItem?.menuItem_Type ?? parentItem?.MenuItem_Type) === "Combo",
    [parentItem]
  );

  // Master (parent branch) values for display when editing overrides
  const masterValues = useMemo(() => {
    if (!parentItem) return null;
    const publish = parentItem?.menuItem_Publish ?? parentItem?.MenuItem_Publish;
    const orderby = parentItem?.menuItem_Orderby ?? parentItem?.MenuItem_Orderby;
    const sale = parentItem?.menuItem_SalePrice ?? parentItem?.MenuItem_SalePrice;
    const delivery = parentItem?.menuItem_DeliveryPrice ?? parentItem?.MenuItem_DeliveryPrice;
    const takeaway = parentItem?.menuItem_TakeAwayPrice ?? parentItem?.MenuItem_TakeAwayPrice;
    const formatPrice = (v) => (v != null && !Number.isNaN(parseFloat(v)) ? parseFloat(v).toFixed(2) : "—");
    return {
      publishActive: publish === true || publish === "true" || publish === 1,
      orderby: orderby != null && orderby !== "" ? String(orderby) : null,
      salePrice: formatPrice(sale),
      deliveryPrice: formatPrice(delivery),
      takeawayPrice: formatPrice(takeaway),
    };
  }, [parentItem]);

  const fetchCompanyProfile = useCallback(async () => {
    const response = await axiosInstance.get(APIs.GET_COMPANY_PROFILE);
    setCompanyProfile(response.data?.data || null);
  }, []);

  const fetchData = useCallback(async () => {
    if (!CompID || !itemId) return;

    try {
      setLoading(true);

      // Ensure we know parentCompId first
      let profile = companyProfile;
      if (!profile) {
        const response = await axiosInstance.get(APIs.GET_COMPANY_PROFILE);
        profile = response.data?.data || null;
        setCompanyProfile(profile);
      }

      const pCompId = toInt(profile?.parentComID ?? profile?.ParentComID);
      if (pCompId <= 0) {
        showErrorToast("This screen is only available in branch mode.");
        navigate("/items/manageItems");
        return;
      }

      const response = await axiosInstance.get(APIs.GET_PRODUCT_WITH_VARIANTS_BY_BRANCH, {
        params: {
          branchCompId: CompID,
          parentCompId: pCompId,
          masterMenuItemId: itemId,
        },
      });

      if (!response.data?.status) {
        showErrorToast(response.data?.message || "Failed to load product details.");
        setParentItem(null);
        setVariantItems([]);
        return;
      }

      const payload = response.data?.data || {};
      const parent = payload.parent ?? payload.Parent ?? null;
      const variants = payload.variants ?? payload.Variants ?? [];

      setParentItem(parent);
      setVariantItems(Array.isArray(variants) ? variants : []);

      setParentForm(buildInitialOverrideForm(parent));

      const vf = {};
      (Array.isArray(variants) ? variants : []).forEach((v) => {
        const id = toInt(v?.menuItem_ID ?? v?.MenuItem_ID);
        if (id > 0) vf[id] = buildInitialOverrideForm(v);
      });
      setVariantForms(vf);
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Failed to load product details.");
    } finally {
      setLoading(false);
    }
  }, [CompID, itemId, companyProfile, navigate]);

  useEffect(() => {
    fetchCompanyProfile().catch(() => {});
  }, [fetchCompanyProfile]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch combo products (items in this deal) when parent is a Combo - use parent company API
  const fetchComboProducts = useCallback(async () => {
    if (!parentCompId || !itemId || !isComboItem) {
      setComboProducts([]);
      return;
    }
    try {
      const response = await axiosInstance.get(APIs.GET_ITEM_BY_ID, {
        params: { CompID: parentCompId, MenuItem_ID: itemId },
      });
      const data = response?.data?.data;
      const products = data?.products ?? data?.Products ?? [];
      setComboProducts(Array.isArray(products) ? products : []);
    } catch (err) {
      setComboProducts([]);
    }
  }, [parentCompId, itemId, isComboItem]);

  useEffect(() => {
    if (isComboItem && parentCompId > 0 && itemId) {
      fetchComboProducts();
    } else {
      setComboProducts([]);
    }
  }, [isComboItem, parentCompId, itemId, fetchComboProducts]);

  const ledgerVariantOptions = useMemo(() => {
    const opts = [{ value: "all", label: "All" }];
    // Only show variants in dropdown, not the parent/grouping item
    variantItems.forEach((v) => {
      const id = toInt(v?.menuItem_ID ?? v?.MenuItem_ID);
      const name = v?.menuItem_DefaultVariantTitle ?? v?.MenuItem_DefaultVariantTitle ?? v?.menuItem_Name ?? v?.MenuItem_Name ?? `Variant #${id}`;
      if (id > 0) opts.push({ value: id, label: name });
    });
    return opts;
  }, [variantItems]);

  const parentItemName = parentItem?.menuItem_Name ?? parentItem?.MenuItem_Name ?? `Item #${itemId}`;
  const showAllLedgerVariants = selectedLedgerVariant?.value === "all" && (hasVariants || ledgerVariantOptions.length > 1);
  const adjustStockMenuItemId = showAllLedgerVariants ? null : (selectedLedgerVariant?.value != null && selectedLedgerVariant?.value !== "all" ? (typeof selectedLedgerVariant.value === "number" ? selectedLedgerVariant.value : parseInt(selectedLedgerVariant.value, 10)) : toInt(parentItem?.menuItem_ID ?? parentItem?.MenuItem_ID ?? itemId));
  const canAdjustStock = !hasVariants || (hasVariants && selectedLedgerVariant?.value !== "all");
  const adjustStockLabel = showAllLedgerVariants ? (selectedLedgerVariant?.label ?? "All") : (selectedLedgerVariant?.label ?? parentItemName);

  const fetchLedgerData = useCallback(async () => {
    if (!CompID || !itemId) return;
    setLedgerLoading(true);
    try {
      const [startDate, endDate] = ledgerDateRange;
      const baseParams = {
        CompID,
        LocationFilter: CompID,
        StartDate: formatCompanyDateTimeForApi(startDate, ledgerTzOptions),
        EndDate: formatCompanyDateTimeForApi(endDate, ledgerTzOptions),
        PageNumber: 1,
        PageSize: 200,
        SortBy: "TransactionDate",
        SortOrder: "DESC",
      };
      const selectedValue = selectedLedgerVariant?.value;
      const isAll = selectedValue === "all" && (hasVariants || ledgerVariantOptions.length > 1);

      if (isAll && ledgerVariantOptions.length > 1) {
        const ids = ledgerVariantOptions.filter((o) => o.value !== "all").map((o) => o.value);
        const promises = ids.map((menuItemId) =>
          axiosInstance.get(APIs.GET_INVENTORY_LEDGER_REPORT, {
            params: { ...baseParams, MenuItemFilter: menuItemId },
          })
        );
        const results = await Promise.allSettled(promises);
        const merged = [];
        results.forEach((r) => {
          if (r.status === "fulfilled" && r.value?.data?.data) {
            const arr = Array.isArray(r.value.data.data) ? r.value.data.data : [];
            merged.push(...arr);
          }
        });
        const byId = {};
        merged.forEach((row) => {
          const id = row.ledgerId ?? row.LedgerId;
          if (id != null && !byId[id]) byId[id] = row;
        });
        const sorted = Object.values(byId).sort((a, b) => getRowDateTimestamp(b) - getRowDateTimestamp(a));
        setLedgerData(sorted);
      } else {
        const menuItemId = selectedValue === "all" ? toInt(parentItem?.menuItem_ID ?? parentItem?.MenuItem_ID ?? itemId) : (typeof selectedValue === "number" ? selectedValue : parseInt(selectedValue, 10));
        const res = await axiosInstance.get(APIs.GET_INVENTORY_LEDGER_REPORT, {
          params: { ...baseParams, MenuItemFilter: menuItemId },
        });
        const data = res?.data?.data ?? res?.data ?? [];
        setLedgerData(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      showErrorToast("Failed to load ledger");
      setLedgerData([]);
    } finally {
      setLedgerLoading(false);
    }
  }, [CompID, itemId, ledgerDateRange, selectedLedgerVariant, hasVariants, ledgerVariantOptions, parentItem, ledgerTzOptions]);

  const handleAdjustSuccess = useCallback(() => {
    fetchLedgerData();
    showSuccessToast("Stock adjusted successfully.");
  }, [fetchLedgerData]);

  useEffect(() => {
    if (activeTab === "ledger" && CompID && itemId) fetchLedgerData();
  }, [activeTab, fetchLedgerData, CompID, itemId, ledgerDateRange, selectedLedgerVariant]);

  const formatLedgerQuantity = (val) => {
    if (val == null || (typeof val !== "number" && isNaN(parseFloat(val)))) return "-";
    const n = parseFloat(val);
    return Number.isInteger(n) ? n.toString() : n.toFixed(2);
  };

  const updateVariantForm = (variantId, patch) => {
    setVariantForms((prev) => ({
      ...prev,
      [variantId]: { ...(prev[variantId] || buildInitialOverrideForm(null)), ...patch },
    }));
  };

  const buildUpdatePayload = (item, form) => {
    const assignmentId = item?.branchAssignmentID ?? item?.BranchAssignmentID ?? null;
    const menuItemId = item?.menuItem_ID ?? item?.MenuItem_ID ?? null;

    const isPublishInherit = form.branchPublishMode === "inherit";
    const publishOverride = isPublishInherit ? null : form.branchPublishMode === "publish";

    const isOrderInherit = form.branchOrderby === "" || form.branchOrderby === null || form.branchOrderby === undefined;
    const orderOverride = isOrderInherit ? null : parseInt(form.branchOrderby, 10);

    return {
      ID: assignmentId,
      MasterMenuItem_ID: menuItemId,
      MasterCompID: parentCompId,
      BranchCompID: CompID,
      IsAssigned: true,
      IsActive: item?.branchAssignmentIsActive ?? item?.BranchAssignmentIsActive ?? true,

      IsPriceOverrideActive: !!form.isPriceOverrideActive,
      BranchPriceOverride: parseNullableFloat(form.branchPriceOverride),
      BranchDeliveryPriceOverride: parseNullableFloat(form.branchDeliveryPriceOverride),
      BranchTakeAwayPriceOverride: parseNullableFloat(form.branchTakeAwayPriceOverride),
      BranchPublish: publishOverride,
      BranchOrderby: Number.isFinite(orderOverride) ? orderOverride : null,

      AssignedBy: assignedBy || "System",
  

      ClearBranchPublish: isPublishInherit,
      ClearBranchOrderby: isOrderInherit,
      ClearBranchPriceOverride:
        !form.isPriceOverrideActive || form.branchPriceOverride === "" || form.branchPriceOverride === null,
      ClearBranchDeliveryPriceOverride:
        !form.isPriceOverrideActive || form.branchDeliveryPriceOverride === "" || form.branchDeliveryPriceOverride === null,
      ClearBranchTakeAwayPriceOverride:
        !form.isPriceOverrideActive || form.branchTakeAwayPriceOverride === "" || form.branchTakeAwayPriceOverride === null,
    };
  };

  const saveAll = async () => {
    if (!isBranchMode) return;
    if (!parentItem) {
      showErrorToast("Product not found.");
      return;
    }

    const updates = [];
    const skipped = [];

    const parentAssignmentId = parentItem?.branchAssignmentID ?? parentItem?.BranchAssignmentID ?? null;
    if (!parentAssignmentId) {
      showErrorToast("Branch assignment not found for this product.");
      return;
    }

    // If item has variants, we do NOT update the main-product overrides (variants only)
    if (!hasVariants) {
      updates.push(
        axiosInstance.put(APIs.PUT_UPDATE_BRANCH_ASSIGNMENT, buildUpdatePayload(parentItem, parentForm))
      );
    }

    variantItems.forEach((v) => {
      const id = toInt(v?.menuItem_ID ?? v?.MenuItem_ID);
      const aId = v?.branchAssignmentID ?? v?.BranchAssignmentID ?? null;
      if (!id) return;
      if (!aId) {
        skipped.push(id);
        return;
      }
      const form = variantForms[id] || buildInitialOverrideForm(v);
      updates.push(axiosInstance.put(APIs.PUT_UPDATE_BRANCH_ASSIGNMENT, buildUpdatePayload(v, form)));
    });

    try {
      setLoading(true);
      const results = await Promise.allSettled(updates);
      const failed = results.filter((r) => r.status === "rejected");

      if (failed.length === 0) {
        if (skipped.length > 0) {
          showSuccessToast("Saved. Some variants were skipped (not assigned to this branch).");
        } else {
          showSuccessToast("Saved branch overrides.");
        }
        await fetchData();
      } else {
        showErrorToast(`Failed to save ${failed.length} update(s).`);
      }
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Failed to save branch overrides.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid">
      {loading && <Preloader />}

      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/" onClick={(e) => e.preventDefault()}>
                Home
              </a>
            </li>
            <li className="breadcrumb-item">
              <a
                href="#/"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/items/manageItems");
                }}
              >
                Items Catalog
              </a>
            </li>
            <li className="breadcrumb-item active">Branch Edit</li>
          </ol>
          <h1 className="page-header mb-0">Branch Edit Item</h1>
          <div className="text-muted" style={{ fontSize: 12 }}>
            Only branch overrides can be edited here (publish, pricing).
          </div>
        </div>

        <div className="ms-auto d-flex gap-2">
          <button
            className="btn btn-outline-secondary"
            onClick={() => navigate(-1)}
            disabled={loading}
            style={{ borderRadius: 10 }}
          >
            Back
          </button>
          <button
            className="btn"
            onClick={saveAll}
            disabled={loading || !isBranchMode}
            style={{
              backgroundColor: themeColor,
              border: "none",
              color: "white",
              borderRadius: 10,
              padding: "8px 14px",
              fontWeight: 600,
            }}
          >
            Save
          </button>
        </div>
      </div>

      {/* Parent summary */}
      <div
        className="mb-3"
        style={{
          padding: 14,
          borderRadius: 14,
          backgroundColor: isDarkMode ? "#1e2124" : "#f8f9fa",
          border: `1px solid ${themeColor}20`,
        }}
      >
        <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
          <div className="fw-bold" style={{ fontSize: 14 }}>
            Product
          </div>
          {!hasVariants && (
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => openVariantInventoryModal(parentItem)}
              disabled={!parentItem?.menuItem_ID && !parentItem?.MenuItem_ID}
              style={{ borderRadius: 10 }}
            >
              View stock
            </button>
          )}
        </div>
        <div style={{ fontSize: 16, fontWeight: 700, marginTop: 4 }}>
          {parentItem?.menuItem_Name || parentItem?.MenuItem_Name || "-"}
        </div>
        <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
          SKU: {parentItem?.menuItem_SKU || parentItem?.MenuItem_SKU || "-"}  
        </div>
      </div>

      {/* Combo: Products in this deal (read-only) */}
      {isComboItem && (
        <div
          className="mb-3"
          style={{
            padding: 14,
            borderRadius: 14,
            backgroundColor: isDarkMode ? "#1e2124" : "#f8f9fa",
            border: `1px solid ${themeColor}20`,
          }}
        >
          <div className="fw-bold mb-2" style={{ fontSize: 14 }}>
            Products in this deal
          </div>
          {comboProducts.length === 0 ? (
            <div className="text-muted" style={{ fontSize: 13 }}>
              No products in this combo. Edit the combo in the parent company to change items.
            </div>
          ) : (
            <ul className="mb-0 ps-3" style={{ fontSize: 13 }}>
              {comboProducts.map((p, idx) => {
                const name = p?.menuItem_DefaultVariantTitle ?? p?.MenuItem_DefaultVariantTitle ?? p?.menuItem_Name ?? p?.MenuItem_Name ?? "-";
                const qty = p?.availableQuantity ?? p?.AvailableQuantity;
                const qtyStr = qty != null && !Number.isNaN(parseFloat(qty)) ? ` × ${parseFloat(qty)}` : "";
                return (
                  <li key={p?.menuItem_ID ?? p?.MenuItem_ID ?? idx} style={{ marginBottom: 4 }}>
                    {name}{qtyStr}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {/* Tabs: Branch overrides | Inventory Ledger */}
      <div className="d-flex gap-2 mb-3" style={{ borderBottom: `2px solid ${isDarkMode ? "#2d3436" : "#e9ecef"}`, paddingBottom: 0 }}>
        <button
          type="button"
          className="btn"
          onClick={() => setActiveTab("overrides")}
          style={{
            borderRadius: "10px 10px 0 0",
            padding: "10px 20px",
            fontWeight: 600,
            fontSize: 14,
            border: "none",
            backgroundColor: activeTab === "overrides" ? themeColor : "transparent",
            color: activeTab === "overrides" ? "#fff" : (isDarkMode ? "#adb5bd" : "#6c757d"),
          }}
        >
          <i className="fa fa-sliders me-2"></i>
          Branch overrides
        </button>
        <button
          type="button"
          className="btn"
          onClick={() => setActiveTab("ledger")}
          style={{
            borderRadius: "10px 10px 0 0",
            padding: "10px 20px",
            fontWeight: 600,
            fontSize: 14,
            border: "none",
            backgroundColor: activeTab === "ledger" ? themeColor : "transparent",
            color: activeTab === "ledger" ? "#fff" : (isDarkMode ? "#adb5bd" : "#6c757d"),
          }}
        >
          <i className="fa fa-book me-2"></i>
          Inventory Ledger
        </button>
      </div>

      {activeTab === "ledger" && (
        <div
          className="mb-4"
          style={{
            padding: 14,
            borderRadius: 14,
            backgroundColor: isDarkMode ? "#1e2124" : "#ffffff",
            border: `1px solid ${themeColor}20`,
          }}
        >
          {ledgerLoading && <Preloader />}
          <div className="row align-items-end g-2 mb-3">
            <div className="col-md-4">
              <label className="form-label mb-0" style={{ color: isDarkMode ? "#fff" : "#000", fontWeight: 500, fontSize: 13 }}>Date range</label>
              <DateRangePicker
                startDate={ledgerDateRange[0]}
                endDate={ledgerDateRange[1]}
                onChange={(update) => {
                  const [newStartDate, newEndDate] = update;
                  if (
                    (!newStartDate || moment(newStartDate).isValid()) &&
                    (!newEndDate || moment(newEndDate).isValid())
                  ) {
                    let effectiveStartDate = newStartDate;
                    let effectiveEndDate = newEndDate;
                    if (newStartDate && newEndDate && newStartDate > newEndDate) {
                      [effectiveStartDate, effectiveEndDate] = [newEndDate, newStartDate];
                    } else if (newStartDate && !newEndDate) {
                      effectiveEndDate = new Date(newStartDate.getTime());
                      effectiveEndDate.setHours(23, 59, 59, 999);
                    }
                    setLedgerDateRange([
                      effectiveStartDate ?? ledgerDateRange[0],
                      effectiveEndDate ?? ledgerDateRange[1],
                    ]);
                  } else {
                    showErrorToast("Invalid date selected");
                  }
                }}
                placeholder="Date range"
                allowSingleDate
                allowDateRange
                showTimeSelection
                className="filter-dropdown"
              />
            </div>
            {ledgerVariantOptions.length > 1 && (
              <div className="col-md-4">
                <label className="form-label mb-0" style={{ color: isDarkMode ? "#fff" : "#000", fontWeight: 500, fontSize: 13 }}>Item / Variant</label>
                <Select
                  value={selectedLedgerVariant}
                  onChange={(opt) => setSelectedLedgerVariant(opt || { value: "all", label: "All" })}
                  options={ledgerVariantOptions}
                  styles={dropdownStyles}
                  isSearchable={false}
                  placeholder="Select"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
            )}
            <div className="col-md-4 d-flex gap-2 align-items-end flex-wrap">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => fetchLedgerData()}
                disabled={ledgerLoading}
                style={{
                  background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                  border: "none",
                  borderRadius: 10,
                  padding: "8px 16px",
                  fontWeight: 600,
                }}
              >
                <i className="fa fa-refresh me-2"></i>
                Refresh
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => canAdjustStock && setShowAdjustModal(true)}
                disabled={!canAdjustStock}
                title={!canAdjustStock ? "Select a single item/variant to adjust stock" : undefined}
                style={{
                  background: canAdjustStock ? `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)` : (isDarkMode ? "#555" : "#ccc"),
                  border: "none",
                  borderRadius: 10,
                  padding: "8px 16px",
                  fontWeight: 600,
                  cursor: canAdjustStock ? "pointer" : "not-allowed",
                }}
              >
                <i className="fa fa-edit me-2"></i>
                Adjust Stock
              </button>
            </div>
          </div>
          <Panel className="mt-2">
            <PanelHeader
              style={{
                backgroundColor: themeColor,
                color: "white",
                borderRadius: "10px 10px 0 0",
                padding: "12px 20px",
                fontWeight: 600,
                fontSize: 15,
              }}
            >
              <i className="fa fa-book me-2"></i>
              Ledger history (this branch)
            </PanelHeader>
            <PanelBody
              style={{
                padding: "16px 20px",
                backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                borderRadius: "0 0 10px 10px",
                border: `1px solid ${themeColor}20`,
              }}
            >
              <div className="table-responsive" style={{ maxHeight: 450, overflowY: "auto" }}>
                <table className="table table-hover" style={{ color: isDarkMode ? "#fff" : "#000", fontSize: 13 }}>
                  <thead style={{ position: "sticky", top: 0, backgroundColor: isDarkMode ? "#3c4043" : "#f8f9fa", zIndex: 10 }}>
                    <tr>
                      <th style={{ padding: "8px 12px" }}>Transaction Date & Time</th>
                      <th style={{ padding: "8px 12px" }}>Type</th>
                      <th className="text-center" style={{ padding: "8px 12px" }}>Quantity Change</th>
                      <th className="text-center" style={{ padding: "8px 12px" }}>Qty Before</th>
                      <th className="text-center" style={{ padding: "8px 12px" }}>Qty After</th>
                      <th style={{ padding: "8px 12px" }}>Unit</th>
                      <th className="text-center" style={{ padding: "8px 12px" }}>Cost Price</th>
                      <th className="text-center" style={{ padding: "8px 12px" }}>Total Cost</th>
                      <th style={{ padding: "8px 12px" }}>Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledgerData.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="text-center py-3 text-muted" style={{ padding: "8px 12px" }}>
                          No ledger entries for this branch in the selected period.
                        </td>
                      </tr>
                    ) : (
                      ledgerData.map((row, idx) => {
                        const qty = parseFloat(row.quantityChange ?? row.QuantityChange ?? 0);
                        const isPos = qty >= 0;
                        return (
                          <tr key={row.ledgerId ?? row.LedgerId ?? idx}>
                            <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                              {formatLedgerRowDateTime(row, ledgerTzOptions)}
                            </td>
                            <td style={{ padding: "8px 12px" }}>{row.transactionType ?? row.TransactionType ?? "-"}</td>
                            <td
                              className="text-center"
                              style={{
                                padding: "8px 12px",
                                color: isPos ? (isDarkMode ? "#55efc4" : "#00b894") : (isDarkMode ? "#ff7675" : "#d63031"),
                                fontWeight: 500,
                              }}
                            >
                              {isPos ? "+" : ""}{formatLedgerQuantity(qty)}
                            </td>
                            <td className="text-center" style={{ fontWeight: 500, padding: "8px 12px" }}>
                              {formatLedgerQuantity(row.quantityBefore ?? row.QuantityBefore)}
                            </td>
                            <td className="text-center" style={{ fontWeight: 500, padding: "8px 12px" }}>
                              {formatLedgerQuantity(row.quantityAfter ?? row.QuantityAfter)}
                            </td>
                            <td style={{ padding: "8px 12px" }}>{row.unit ?? row.Unit ?? "-"}</td>
                            <td className="text-center" style={{ padding: "8px 12px" }}>
                              {row.costPrice != null || row.CostPrice != null
                                ? `${getCurrencySymbol(compCurrency)}${parseFloat(row.costPrice ?? row.CostPrice ?? 0).toFixed(4)}`
                                : "-"}
                            </td>
                            <td className="text-center" style={{ padding: "8px 12px" }}>
                              {row.totalCost != null || row.TotalCost != null
                                ? `${getCurrencySymbol(compCurrency)}${parseFloat(row.totalCost ?? row.TotalCost ?? 0).toFixed(2)}`
                                : "-"}
                            </td>
                            <td style={{ padding: "8px 12px" }}>{row.transactionReference ?? row.TransactionReference ?? "-"}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </PanelBody>
          </Panel>
        </div>
      )}

      {activeTab === "overrides" && (
        <>
      {/* Parent overrides (hidden when item has variants; variants-only override model) */}
      {!hasVariants && (
        <div
          className="mb-4"
          style={{
            padding: 14,
            borderRadius: 14,
            backgroundColor: isDarkMode ? "#1e2124" : "#ffffff",
            border: `1px solid ${themeColor}20`,
          }}
        >
          <div className="fw-bold mb-2" style={{ fontSize: 14 }}>
            Parent overrides
          </div>

          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <label className="form-label fw-bold">Publish override</label>
              {masterValues && (
                <div className="text-muted small mb-1" style={{ fontSize: 12 }}>
                  Parent branch: <strong style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>{masterValues.publishActive ? "Active" : "Inactive"}</strong>
                </div>
              )}
              <select
                className="form-select"
                value={parentForm.branchPublishMode}
                onChange={(e) => setParentForm((p) => ({ ...p, branchPublishMode: e.target.value }))}
                style={{
                  borderRadius: 12,
                  backgroundColor: isDarkMode ? "#1e2124" : "#ffffff",
                  border: `1px solid ${themeColor}30`,
                  color: isDarkMode ? "#ffffff" : "#000000",
                }}
              >
                <option value="inherit">Inherit (use master)</option>
                <option value="publish">Publish</option>
                <option value="unpublish">Unpublish</option>
              </select>
            </div>

            {/* Sort order override - hidden per requirement
            <div className="col-md-6">
              <label className="form-label fw-bold">Sort order override</label>
              {masterValues && (
                <div className="text-muted small mb-1" style={{ fontSize: 12 }}>
                  Parent branch: <strong style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>{masterValues.orderby != null ? masterValues.orderby : "(not set)"}</strong>
                </div>
              )}
              <input
                type="number"
                className="form-control"
                value={parentForm.branchOrderby}
                onChange={(e) => setParentForm((p) => ({ ...p, branchOrderby: e.target.value }))}
                placeholder="Leave empty to inherit"
                style={{
                  borderRadius: 12,
                  backgroundColor: isDarkMode ? "#1e2124" : "#ffffff",
                  border: `1px solid ${themeColor}30`,
                  color: isDarkMode ? "#ffffff" : "#000000",
                }}
              />
            </div>
            */}
          </div>

        <div
          style={{
            padding: 14,
            borderRadius: 14,
            backgroundColor: isDarkMode ? "#1e2124" : "#ffffff",
            border: `1px solid ${themeColor}20`,
          }}
        >
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div>
              <div className="fw-bold" style={{ fontSize: 14 }}>
                Pricing overrides
              </div>
              <div className="text-muted" style={{ fontSize: 12, marginTop: 2 }}>
                Overrides apply only when enabled and value is provided.
              </div>
            </div>
            <ToggleSwitch
              isChecked={!!parentForm.isPriceOverrideActive}
              onToggle={(checked) => setParentForm((p) => ({ ...p, isPriceOverrideActive: checked }))}
              label="Enable"
              name="parentPriceOverrideActive"
              size="small"
            />
          </div>

          <div className="row g-3 mt-1">
            <div className="col-md-4">
              <label className="form-label fw-bold">Sale price override</label>
              {masterValues && (
                <div className="text-muted small mb-1" style={{ fontSize: 12 }}>
                  Parent branch: <strong style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>{getCurrencySymbol(compCurrency)}{masterValues.salePrice}</strong>
                </div>
              )}
              <input
                type="number"
                className="form-control"
                value={parentForm.branchPriceOverride}
                onChange={(e) => setParentForm((p) => ({ ...p, branchPriceOverride: e.target.value }))}
                placeholder="Leave empty to inherit"
                style={{
                  borderRadius: 12,
                  backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                  border: `1px solid ${themeColor}30`,
                  color: isDarkMode ? "#ffffff" : "#000000",
                }}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-bold">Delivery price override</label>
              {masterValues && (
                <div className="text-muted small mb-1" style={{ fontSize: 12 }}>
                  Parent branch: <strong style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>{getCurrencySymbol(compCurrency)}{masterValues.deliveryPrice}</strong>
                </div>
              )}
              <input
                type="number"
                className="form-control"
                value={parentForm.branchDeliveryPriceOverride}
                onChange={(e) => setParentForm((p) => ({ ...p, branchDeliveryPriceOverride: e.target.value }))}
                placeholder="Leave empty to inherit"
                style={{
                  borderRadius: 12,
                  backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                  border: `1px solid ${themeColor}30`,
                  color: isDarkMode ? "#ffffff" : "#000000",
                }}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-bold">Takeaway price override</label>
              {masterValues && (
                <div className="text-muted small mb-1" style={{ fontSize: 12 }}>
                  Parent branch: <strong style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>{getCurrencySymbol(compCurrency)}{masterValues.takeawayPrice}</strong>
                </div>
              )}
              <input
                type="number"
                className="form-control"
                value={parentForm.branchTakeAwayPriceOverride}
                onChange={(e) => setParentForm((p) => ({ ...p, branchTakeAwayPriceOverride: e.target.value }))}
                placeholder="Leave empty to inherit"
                style={{
                  borderRadius: 12,
                  backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                  border: `1px solid ${themeColor}30`,
                  color: isDarkMode ? "#ffffff" : "#000000",
                }}
              />
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Variants (only when item actually has variants) */}
      {hasVariants && (
        <div
          style={{
            padding: 14,
            borderRadius: 14,
            backgroundColor: isDarkMode ? "#1e2124" : "#ffffff",
            border: `1px solid ${themeColor}20`,
          }}
        >
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div className="fw-bold" style={{ fontSize: 14 }}>
              Variants
            </div>
            <div className="text-muted" style={{ fontSize: 12 }}>
              {variantItems.length} variant(s)
            </div>
          </div>

          {variantItems.length === 0 ? (
            <div className="text-muted" style={{ fontSize: 13 }}>
              No variants found.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Variant Title</th>
                    <th style={{ width: 160 }}>Publish</th>
                    {/* Sort column hidden per requirement
                    <th style={{ width: 140 }}>Sort</th>
                    */}
                    <th style={{ width: 160 }}>Price override</th>
                    <th style={{ width: 160 }}>Sale</th>
                    <th style={{ width: 160 }}>Delivery</th>
                    <th style={{ width: 160 }}>Takeaway</th>
                    <th style={{ width: 140 }}>Inventory</th>
                  </tr>
                </thead>
                <tbody>
                  {variantItems.map((v) => {
                    const vid = toInt(v?.menuItem_ID ?? v?.MenuItem_ID);
                    const form = variantForms[vid] || buildInitialOverrideForm(v);
                    const assignmentId = v?.branchAssignmentID ?? v?.BranchAssignmentID ?? null;
                    const disabled = !assignmentId;
                    const vPublish = v?.menuItem_Publish ?? v?.MenuItem_Publish;
                    const vOrderby = v?.menuItem_Orderby ?? v?.MenuItem_Orderby;
                    const formatVPrice = (val) => (val != null && !Number.isNaN(parseFloat(val)) ? parseFloat(val).toFixed(2) : "—");
                    const vSale = formatVPrice(v?.menuItem_SalePrice ?? v?.MenuItem_SalePrice);
                    const vDelivery = formatVPrice(v?.menuItem_DeliveryPrice ?? v?.MenuItem_DeliveryPrice);
                    const vTakeaway = formatVPrice(v?.menuItem_TakeAwayPrice ?? v?.MenuItem_TakeAwayPrice);
                    const variantParentPublishActive = vPublish === true || vPublish === "true" || vPublish === 1;

                    return (
                      <tr key={vid || Math.random()}>
                        <td className="align-middle">
                          <div className="fw-bold" style={{ fontSize: 13 }}>
                            {v?.menuItem_DefaultVariantTitle || v?.MenuItem_DefaultVariantTitle || "-"}
                          </div>
                          <div className="text-muted" style={{ fontSize: 12 }}>
                            {v?.menuItem_Name || v?.MenuItem_Name || "-"}
                          </div>
                        </td>

                        <td className="align-middle">
                          <div className="text-muted small mb-1" style={{ fontSize: 11 }}>Parent: {variantParentPublishActive ? "Active" : "Inactive"}</div>
                          <select
                            className="form-select"
                            value={form.branchPublishMode}
                            disabled={disabled}
                            onChange={(e) => updateVariantForm(vid, { branchPublishMode: e.target.value })}
                            style={{
                              borderRadius: 10,
                              backgroundColor: isDarkMode ? "#1e2124" : "#ffffff",
                              border: `1px solid ${themeColor}30`,
                              color: isDarkMode ? "#ffffff" : "#000000",
                            }}
                          >
                            <option value="inherit">Inherit</option>
                            <option value="publish">Publish</option>
                            <option value="unpublish">Unpublish</option>
                          </select>
                        </td>

                        {/* Sort order column hidden per requirement
                        <td className="align-middle">
                          <div className="text-muted small mb-1" style={{ fontSize: 11 }}>Parent: {vOrderby != null && vOrderby !== "" ? String(vOrderby) : "—"}</div>
                          <input
                            type="number"
                            className="form-control"
                            value={form.branchOrderby}
                            disabled={disabled}
                            onChange={(e) => updateVariantForm(vid, { branchOrderby: e.target.value })}
                            placeholder="inherit"
                            style={{
                              borderRadius: 10,
                              backgroundColor: isDarkMode ? "#1e2124" : "#ffffff",
                              border: `1px solid ${themeColor}30`,
                              color: isDarkMode ? "#ffffff" : "#000000",
                            }}
                          />
                        </td>
                        */}

                        <td className="align-middle">
                          <ToggleSwitch
                            isChecked={!!form.isPriceOverrideActive}
                            onToggle={(checked) => updateVariantForm(vid, { isPriceOverrideActive: checked })}
                            label="Enable"
                            name={`variantPriceOverrideActive_${vid}`}
                            size="small"
                            disabled={disabled}
                          />
                        </td>

                        <td className="align-middle">
                          <div className="text-muted small mb-1" style={{ fontSize: 11 }}>Parent: {getCurrencySymbol(compCurrency)}{vSale}</div>
                          <input
                            type="number"
                            className="form-control"
                            value={form.branchPriceOverride}
                            disabled={disabled || !form.isPriceOverrideActive}
                            onChange={(e) => updateVariantForm(vid, { branchPriceOverride: e.target.value })}
                            placeholder="inherit"
                            style={{
                              borderRadius: 10,
                              backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                              border: `1px solid ${themeColor}30`,
                              color: isDarkMode ? "#ffffff" : "#000000",
                            }}
                          />
                        </td>

                        <td className="align-middle">
                          <div className="text-muted small mb-1" style={{ fontSize: 11 }}>Parent: {getCurrencySymbol(compCurrency)}{vDelivery}</div>
                          <input
                            type="number"
                            className="form-control"
                            value={form.branchDeliveryPriceOverride}
                            disabled={disabled || !form.isPriceOverrideActive}
                            onChange={(e) => updateVariantForm(vid, { branchDeliveryPriceOverride: e.target.value })}
                            placeholder="inherit"
                            style={{
                              borderRadius: 10,
                              backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                              border: `1px solid ${themeColor}30`,
                              color: isDarkMode ? "#ffffff" : "#000000",
                            }}
                          />
                        </td>

                        <td className="align-middle">
                          <div className="text-muted small mb-1" style={{ fontSize: 11 }}>Parent: {getCurrencySymbol(compCurrency)}{vTakeaway}</div>
                          <input
                            type="number"
                            className="form-control"
                            value={form.branchTakeAwayPriceOverride}
                            disabled={disabled || !form.isPriceOverrideActive}
                            onChange={(e) => updateVariantForm(vid, { branchTakeAwayPriceOverride: e.target.value })}
                            placeholder="inherit"
                            style={{
                              borderRadius: 10,
                              backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                              border: `1px solid ${themeColor}30`,
                              color: isDarkMode ? "#ffffff" : "#000000",
                            }}
                          />
                        </td>

                        <td className="align-middle">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => openVariantInventoryModal(v)}
                            disabled={!vid}
                            style={{ borderRadius: 10 }}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
        </>
      )}

      <VariantInventoryModal
        isOpen={variantInventoryModalOpen}
        onClose={closeVariantInventoryModal}
        mode="branch"
        parentCompId={parentCompId}
        fixedBranchCompId={CompID}
        variant={selectedVariantForInventory}
      />

      {adjustStockMenuItemId != null && adjustStockMenuItemId > 0 && (
        <AdjustStockModal
          isOpen={showAdjustModal}
          onClose={() => setShowAdjustModal(false)}
          onSuccess={handleAdjustSuccess}
          menuItemId={adjustStockMenuItemId}
          compID={CompID}
          itemName={adjustStockLabel}
        />
      )}
    </div>
  );
};

export default BranchEditItem;

